import React, {useMemo, useState} from "react";
import Project from "../models/Project";
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useMount, useUnmount} from "react-use";
import ProjectPosition, {generateScopingForDeliverables, updateContractTerms} from "../models/ProjectPosition";
import {useAuth} from "./AuthProvider";
import {useProjectForm} from "./ProjectFormProvider";
import LoadingBanner from "../components/LoadingBanner";

let PositionFormContext = React.createContext(null);

const PositionFormProvider = ({children, positionId, formRef}) => {
    const {
        project,
        formModel: projectFormModel,
        isLoading: isProjectLoading,
        isProjectUpdating,
        onFormSubmit: onProjectFormSubmit
    } = useProjectForm();

    const allParams = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const {projectId} = useParams();

    const queryClient = useQueryClient();
    const {auth, update: updateAuth} = useAuth();

    const [isLoading, setIsLoading] = useState(true);
    const [isDisabled, setIsDisabled] = useState(false);

    const [formModel, setFormModel] = useState(null);
    const [position, setPosition] = useState(null);

    useMount(async() => {
        console.log('PositionFormProvider @ useMount')

        if (TB.isDefined(positionId) && positionId !== 'new') {
            let matchedPosition = TB.find(projectFormModel.sortedPositions(),['id',parseInt(positionId)])
            if (matchedPosition) {
                await setFormModel(ProjectPosition.make({...matchedPosition, contract_type: project.contract_type}))
                await setPosition(ProjectPosition.make({...matchedPosition, contract_type: project.contract_type}))
            }
        }else if(positionId === 'new') {
            let newPosition = project.newPosition(project.nextPositionDisplayIndexFrom(project.positions));
            await setFormModel(ProjectPosition.make({...newPosition, contract_type: project.contract_type}))
            await setPosition(ProjectPosition.make({...newPosition, contract_type: project.contract_type}))
        }
        await setIsLoading(false)
    })

    const updatedProjectCallback = async (updatedProject) => {
        let contract_type = updatedProject.contract_type;

        let matchedPosition = TB.find(updatedProject.sortedPositions(),['id',parseInt(positionId)]);
        if (matchedPosition) {
            await setFormModel(ProjectPosition.make({...matchedPosition, contract_type}))
            await setPosition(ProjectPosition.make({...matchedPosition, contract_type}))
        }else if (formModel && contract_type !== formModel.contract_type) {
            await setFormModel(ProjectPosition.make({...formModel, contract_type}))
        }
    }
    const [uniqId, setUniqId] = useState(TB.uuid());

    useMount(() => {
        TB.event().listen("Projects@updated", updatedProjectCallback, uniqId)
    })

    useUnmount(() => {
        TB.event().provider().off("Projects@updated", updatedProjectCallback, uniqId)
    })

    const onFormSubmit = async(positionProps) => {
        await setIsDisabled(true);

        let updatedProject;

        try {
            let updatedPosition = ProjectPosition.make({...formModel, ...positionProps});

            let positions = [...(projectFormModel.sortedPositions())];

            let matchedIndex = TB.findIndex(projectFormModel.sortedPositions(true),['id',parseInt(positionId)])

            if (positionId === 'new' && !updatedPosition?.id) {
                positions.push(
                    ProjectPosition.make({...formModel, ...positionProps})
                )
            }else if(updatedPosition?.id) {

                if (project.isBidding()) {
                    console.log('CHECKING PRICING')
                    let confirmedPricingChanges = await position.checkIfPricingAffectsCurrentCandidates(positionProps)

                    if (!confirmedPricingChanges) {
                        await setIsDisabled(false);

                        return false;
                    }
                }
                positions = TB.map(positions, pos => {
                    if(pos.id === updatedPosition?.id) {
                        return updatedPosition;
                    }
                    return pos;
                })
            }else if(matchedIndex !== -1) {
                positions[matchedIndex] = updatedPosition
            }

            updatedProject = await onProjectFormSubmit({positions})
        }catch (e) {
            console.error('onPositionFormSubmit', e);
        }

        if(!updatedProject){
            await setIsDisabled(false);

            let PostRequest = Project.SyncRequest
            PostRequest.didHandleError = await PostRequest.handleError();
            if (PostRequest.didHandleError
                && PostRequest.shouldRerun) {
                return onFormSubmit(positionProps)
            }else return;
        }

        if(positionId === 'new') {
            await setIsDisabled(false);
            let savedPosition = TB.last(updatedProject.sortedPositions(true));
            await TB.toast("Project Role Added")
            return navigate({
                ...location,
                pathname: `/client/project/setup/${updatedProject?.id}/role/${savedPosition?.id}`
            }, {replace: true})
        }
        let savedPosition = TB.find(updatedProject.sortedPositions(),['id',parseInt(positionId)]);

        await setPosition(savedPosition);
        await setFormModel(ProjectPosition.make({...savedPosition}))
        await setIsDisabled(false);

        TB.toast("Project Role Saved")
        return savedPosition;
    }

    const updateContractTermsMutation = useMutation(['updateContractTerms'],
        async(params) => {
            await setIsDisabled(true);
            return updateContractTerms(projectId,positionId, params)
        }, {
            onError: async(e) => {
                await setIsDisabled(false)
                return  TB.showServerErrorToast()
            },
            onSuccess: async(updatedPosition) => {
                await setPosition(updatedPosition)
                await setFormModel(ProjectPosition.make({...updatedPosition}));
                await setIsDisabled(false)
                // await updateCallback(updatedProject);
            }
        }
    )
    const generateScopingMutation = useMutation(['generateScoping'],
        async() => {
            await setIsDisabled(true);
            let scopedDeliverables = await generateScopingForDeliverables(projectId, positionId).catch(console.warn);

            if (scopedDeliverables) {
                let updatedPosition = ProjectPosition.make({...position, deliverables: TB.map(position.deliverables, del => {
                        let matchingDel = TB.find(scopedDeliverables, scopedDel => parseInt(scopedDel.id) === parseInt(del.id));
                        if (!matchingDel) {
                            return del;
                        }
                        if (position.fixedFeePricingStrategyIsFullBreakdown()) {
                            return {
                                ...del,
                                total_hours: matchingDel.total_hours,
                                payout_amount: matchingDel.payout_amount,
                            }
                        }else if (position.fixedFeePricingStrategyIsPayoutByDeliverable()) {
                            return {
                                ...del,
                                total_hours: null,
                                payout_amount: matchingDel.payout_amount,
                            }
                        }
                        return del;
                    })})

                await setFormModel(updatedPosition);
                let submitted = await onFormSubmit(updatedPosition);

                if (submitted) {
                    TB.queryClient.invalidateQueries({
                        predicate: ({queryKey}) => {
                            let params = queryKey?.[1] || {};
                            return queryKey?.[0] === 'positionMatches' && parseInt(params.project_position) === parseInt(position.id);
                        }
                    });
                }
            }
        }, {
            onError: async(e) => {
                await setIsDisabled(false)
                return  TB.showServerErrorToast()
            },
            onSuccess: async(deliverables) => {
                // TB.toast()
            }
        }
    )

    const canCopyPrevious = useMemo(() => {
        return positionId === 'new' && projectFormModel.sortedPositions(true).length > 0
    }, [projectId, projectFormModel?.positions?.length]);

    const canSetContractType = useMemo(() => {
        // if (positionId !== 'new' && TB.findIndex(projectFormModel?.positions, ['id', parseInt(positionId)]) === 0) {
        //     return true;
        // }
        return positionId === 'new' && projectFormModel.sortedPositions(true).length === 0
    }, [projectId, projectFormModel?.positions?.length]);

    const copyPreviousPosition = async() => {

        if (isDisabled || isProjectUpdating) return false;

        if(positionId !== 'new' || !projectFormModel.sortedPositions(true).length) {
            return false
        }
            let {
                skills,
                disciplines,
                templates,
                deliverables,
                budget,
                description_path,
                industries,
                description,
                max_rate,
                target_rate,
                target_rate_is_visible,
                avg_hours_per_period,
                required_onsite,
                started_at,
                ended_at,
                contract_type,
                onsite_days_per_week,
                wants_intermediate_experience,
                wants_senior_experience,
                wants_junior_experience
            } = TB.last(projectFormModel.sortedPositions(true));

        return onFormSubmit({
            skills,
            disciplines,
            contract_type,
            templates,
            deliverables,
            budget,
            industries,
            description,
            description_path,
            max_rate,
            target_rate,
            target_rate_is_visible,
            avg_hours_per_period,
            required_onsite,
            started_at,
            ended_at,
            onsite_days_per_week,
            wants_intermediate_experience,
            wants_senior_experience,
            wants_junior_experience
        })
    }

    const value = {
        position,
        formModel,
        isLoading,
        isDisabled,
        canCopyPrevious,
        canSetContractType,
        hasFormBeenSubmitted: formRef && formRef.current?.isFormSubmitted(),
        generateScoping: () => generateScopingMutation.mutateAsync({}),
        copyPreviousPosition,
        onFormChange: (params) => setFormModel(ProjectPosition.make({...formModel, ...params})),
        isProjectUpdating: isDisabled || isProjectUpdating || updateContractTermsMutation.isLoading || generateScopingMutation.isLoading,
        onFormReset: () => setFormModel(ProjectPosition.make({...position})),
        onFormSubmit,
        updateContractTerms: (params) => updateContractTermsMutation.mutate(params),
        deletePosition: async() => {
            if (project.sortedPositions?.().length <= 1) {
                TB.toast('You must have at least one role', {theme: "danger"});
                return false;
            }
            let updatedProject = await project.confirmDeletePosition(
                position
            );

            if (!updatedProject) {
                return;
            }
            return true;
        }
        // error,
        // refetch,
        // postUpdate,

        // projectError: error,

    }

    if (isLoading) {
        return <LoadingBanner message="Loading" adjustOverlay/>;
    }

    return (
        <PositionFormContext.Provider value={value}>
            {children}
        </PositionFormContext.Provider>
    );
}

const usePositionForm = () => {
    return React.useContext(PositionFormContext);
}
export default PositionFormProvider;
export {
    usePositionForm,
    PositionFormContext
}